import { useState, useEffect } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useApp } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function RegisterPage() {
  const { register, user, t } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || "";
  const from = location.state?.from || "/";

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: email,
    password: "",
    repeat_password: "",
    phone: "",
    address: "",
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          clearInterval(id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const allRequiredFilled = () => {
    return (
      form.first_name.trim() &&
      form.last_name.trim() &&
      form.email.trim() &&
      form.password &&
      form.repeat_password &&
      form.phone.trim()
    );
  };

  const passwordsMatch = form.password === form.repeat_password;
  const canSubmit = allRequiredFilled() && passwordsMatch && acceptedTerms && !loading && cooldown <= 0;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!allRequiredFilled()) {
      toast.error(t("Please fill all required fields"));
      return;
    }

    if (form.password.length < 8) {
      toast.error(t("Password must be at least 8 characters"));
      return;
    }

    if (form.password !== form.repeat_password) {
      toast.error(t("Passwords do not match"));
      return;
    }

    if (!acceptedTerms) {
      toast.error(t("You must accept the terms"));
      return;
    }

    setLoading(true);
    try {
      await register({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        phone: form.phone.trim(),
        address: form.address.trim() || undefined,
      });
      toast.success(t("Account created!"));
      navigate(from, { replace: true });
    } catch (e) {
      if (e.response?.status === 429) {
        toast.error(t("Too many attempts. Please wait a minute before trying again."));
        setCooldown(60);
      } else {
        toast.error(e.response?.data?.detail || t("Something went wrong"));
      }
    } finally {
      setLoading(false);
    }
  };

  if (user) {
    return <Navigate to="/profile" replace />;
  }

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  return (
    <div className="p-6 max-w-md mx-auto" data-testid="register-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="font-playfair text-3xl font-bold text-[#EDEDED] mb-2">
          {t("Create account")}
        </h1>
        <p className="text-[#D4D4D8] mb-6 text-sm">
          {t("Join biten for the best dining experience")}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[#D4D4D8] text-xs mb-1">
                {t("First Name")} <span className="text-[#EF4444]">*</span>
              </Label>
              <Input value={form.first_name} onChange={set("first_name")}
                placeholder="John" className="bg-[#121212] border-[#27272A] text-[#EDEDED] h-11" data-testid="input-first-name" required />
            </div>
            <div>
              <Label className="text-[#D4D4D8] text-xs mb-1">
                {t("Last Name")} <span className="text-[#EF4444]">*</span>
              </Label>
              <Input value={form.last_name} onChange={set("last_name")}
                placeholder="Doe" className="bg-[#121212] border-[#27272A] text-[#EDEDED] h-11" data-testid="input-last-name" required />
            </div>
          </div>

          <div>
            <Label className="text-[#D4D4D8] text-xs mb-1">
              {t("Email")} <span className="text-[#EF4444]">*</span>
            </Label>
            <Input type="email" value={form.email} onChange={set("email")}
              placeholder="you@email.com" className="bg-[#121212] border-[#27272A] text-[#EDEDED] h-11" data-testid="input-email" required />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[#D4D4D8] text-xs mb-1">
                {t("Password")} <span className="text-[#EF4444]">*</span>
              </Label>
              <Input type="password" value={form.password} onChange={set("password")}
                placeholder="Min 6 characters" className="bg-[#121212] border-[#27272A] text-[#EDEDED] h-11" data-testid="input-password" required />
            </div>
            <div>
              <Label className="text-[#D4D4D8] text-xs mb-1">
                {t("Repeat Password")} <span className="text-[#EF4444]">*</span>
              </Label>
              <Input type="password" value={form.repeat_password} onChange={set("repeat_password")}
                placeholder="Repeat password" className={`bg-[#121212] border-[#27272A] text-[#EDEDED] h-11 ${form.repeat_password && !passwordsMatch ? "border-[#EF4444]" : ""}`} data-testid="input-repeat-password" required />
              {form.repeat_password && !passwordsMatch && (
                <p className="text-[#EF4444] text-[11px] mt-1">{t("Passwords do not match")}</p>
              )}
            </div>
          </div>

          <div>
            <Label className="text-[#D4D4D8] text-xs mb-1">
              {t("Phone Number")} <span className="text-[#EF4444]">*</span>
            </Label>
            <Input value={form.phone} onChange={set("phone")}
              placeholder="+46 70 123 4567" className="bg-[#121212] border-[#27272A] text-[#EDEDED] h-11" data-testid="input-phone" required />
          </div>

          <div>
            <Label className="text-[#D4D4D8] text-xs mb-1">{t("Address")}</Label>
            <Input value={form.address} onChange={set("address")}
              placeholder="Street address (optional)" className="bg-[#121212] border-[#27272A] text-[#EDEDED] h-11" data-testid="input-address" />
          </div>

          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="terms"
              checked={acceptedTerms}
              onChange={e => setAcceptedTerms(e.target.checked)}
              className="mt-1 accent-[#D4B483] w-4 h-4"
              data-testid="input-terms"
            />
            <label htmlFor="terms" className="text-[#D4D4D8] text-xs leading-relaxed">
              {t("I accept the")}{" "}
              <a href="/terms-of-service" target="_blank" className="text-[#D4B483] hover:underline">{t("Terms of Service")}</a>
              {" "}{t("and")}{" "}
              <a href="/privacy-policy" target="_blank" className="text-[#D4B483] hover:underline">{t("Privacy Policy")}</a>
            </label>
          </div>

          <Button type="submit" disabled={!canSubmit} className="w-full bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold h-12 disabled:opacity-50" data-testid="register-submit-btn">
            {cooldown > 0 ? `${t("Wait")} ${cooldown}s` : loading ? t("Please wait...") : t("Create Account")}
          </Button>
        </form>

        <p className="text-center text-[#D4D4D8] text-sm mt-6">
          {t("Already have an account?")}{" "}
          <a href="/auth" className="text-[#D4B483] font-medium hover:underline">
            {t("Sign In")}
          </a>
        </p>
      </motion.div>
    </div>
  );
}
